import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Pencil, X, Check, Calendar, Clock, ExternalLink, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import api from '../lib/api';

const AdminClassCalendar = ({ courseSlug, courseId }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendarEnabled, setCalendarEnabled] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: '', session_date: '', start_time: '', end_time: '', meet_url: '' });

  useEffect(() => {
    fetchSessions();
  }, [courseSlug]);

  const fetchSessions = async () => {
    try {
      const res = await api.get(`/special-courses/${courseSlug}/class-sessions`);
      setSessions(res.data.sessions || []);
      setCalendarEnabled(res.data.calendar_enabled !== false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCalendar = async () => {
    setToggling(true);
    try {
      const res = await api.put(`/admin/special-courses/${courseId || courseSlug}/toggle-calendar`);
      setCalendarEnabled(res.data.calendar_enabled);
      toast.success(res.data.message);
    } catch (err) {
      toast.error('Error al cambiar estado del calendario');
    } finally {
      setToggling(false);
    }
  };

  const handleAdd = async () => {
    if (!form.title || !form.session_date) {
      toast.error('Título y fecha son obligatorios');
      return;
    }
    try {
      const res = await api.post(`/admin/special-courses/${courseSlug}/class-sessions`, form);
      setSessions(prev => [...prev, res.data].sort((a, b) => (a.session_date || '').localeCompare(b.session_date || '')));
      resetForm();
      toast.success('Sesión añadida');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al añadir sesión');
    }
  };

  const handleUpdate = async (sessionId) => {
    try {
      const res = await api.put(`/admin/special-courses/${courseSlug}/class-sessions/${sessionId}`, form);
      setSessions(prev => prev.map(s => s.id === sessionId ? res.data : s).sort((a, b) => (a.session_date || '').localeCompare(b.session_date || '')));
      resetForm();
      toast.success('Sesión actualizada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleDelete = async (sessionId) => {
    if (!window.confirm('¿Eliminar esta sesión?')) return;
    try {
      await api.delete(`/admin/special-courses/${courseSlug}/class-sessions/${sessionId}`);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      toast.success('Sesión eliminada');
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const handleMarkCompleted = async (session) => {
    try {
      const res = await api.put(`/admin/special-courses/${courseSlug}/class-sessions/${session.id}`, {
        is_completed: !session.is_completed
      });
      setSessions(prev => prev.map(s => s.id === session.id ? res.data : s));
      toast.success(res.data.is_completed ? 'Marcada como realizada' : 'Marcada como pendiente');
    } catch (err) {
      toast.error('Error al actualizar');
    }
  };

  const startEdit = (session) => {
    setEditingId(session.id);
    setShowForm(true);
    setForm({
      title: session.title || '',
      session_date: session.session_date || '',
      start_time: session.start_time || '',
      end_time: session.end_time || '',
      meet_url: session.meet_url || '',
    });
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({ title: '', session_date: '', start_time: '', end_time: '', meet_url: '' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
    } catch { return dateStr; }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-[hsl(var(--accent))] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="card-soft" data-testid="admin-class-calendar">
      {/* Header with toggle */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-heading text-[hsl(var(--text-primary))]">Calendario de Clases</h2>
          <p className="text-sm text-[hsl(var(--text-tertiary))]">{sessions.length} {sessions.length === 1 ? 'sesión' : 'sesiones'} programadas</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Calendar toggle */}
          <button
            onClick={handleToggleCalendar}
            disabled={toggling}
            className="flex items-center gap-2"
            data-testid="toggle-calendar-btn"
          >
            {toggling ? (
              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
            ) : calendarEnabled ? (
              <ToggleRight className="w-8 h-8 text-green-500" />
            ) : (
              <ToggleLeft className="w-8 h-8 text-gray-400" />
            )}
            <span className={`text-xs font-medium ${calendarEnabled ? 'text-green-600' : 'text-gray-400'}`}>
              {calendarEnabled ? 'Visible' : 'Oculto'}
            </span>
          </button>
          {!showForm && (
            <Button
              onClick={() => { setShowForm(true); setEditingId(null); setForm({ title: '', session_date: '', start_time: '18:00', end_time: '20:00', meet_url: '' }); }}
              className="btn-gold gap-2"
              data-testid="admin-add-session-btn"
            >
              <Plus className="w-4 h-4" /> Añadir sesión
            </Button>
          )}
        </div>
      </div>

      {!calendarEnabled && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm text-amber-700">
          El calendario está <strong>oculto</strong> para las alumnas. Actívalo con el interruptor de arriba.
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <div className="mb-6 bg-[hsl(var(--sage))]/10 border border-[hsl(var(--sage))]/20 rounded-xl p-5" data-testid="admin-session-form">
          <h3 className="text-sm font-medium text-[hsl(var(--text-primary))] mb-3">{editingId ? 'Editar sesión' : 'Nueva sesión de clase'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[hsl(var(--text-tertiary))] mb-1 block">Título *</label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Clase 1 - Iniciación" data-testid="session-title-input" />
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--text-tertiary))] mb-1 block">Fecha *</label>
              <Input type="date" value={form.session_date} onChange={e => setForm(f => ({ ...f, session_date: e.target.value }))} data-testid="session-date-input" />
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--text-tertiary))] mb-1 block">Hora inicio</label>
              <Input type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} data-testid="session-start-input" />
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--text-tertiary))] mb-1 block">Hora fin</label>
              <Input type="time" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))} data-testid="session-end-input" />
            </div>
          </div>
          <div className="mt-3">
            <label className="text-xs text-[hsl(var(--text-tertiary))] mb-1 block">Enlace de Google Meet</label>
            <Input value={form.meet_url} onChange={e => setForm(f => ({ ...f, meet_url: e.target.value }))} placeholder="https://meet.google.com/xxx-xxxx-xxx" data-testid="session-meet-input" />
          </div>
          <div className="flex gap-2 mt-3">
            <Button onClick={() => editingId ? handleUpdate(editingId) : handleAdd()} className="btn-gold gap-1 text-sm" data-testid="session-save-btn">
              <Check className="w-3.5 h-3.5" /> {editingId ? 'Guardar' : 'Añadir'}
            </Button>
            <Button onClick={resetForm} variant="outline" className="gap-1 text-sm" data-testid="session-cancel-btn">
              <X className="w-3.5 h-3.5" /> Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* Session List */}
      {sessions.length === 0 && !showForm ? (
        <div className="text-center py-8 bg-gray-50/50 rounded-lg">
          <Calendar className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-[hsl(var(--text-tertiary))]">No hay sesiones programadas</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="admin-session-list">
          {sessions.map(session => (
            <div
              key={session.id}
              className={`flex items-center gap-3 p-3 border rounded-lg transition-colors ${
                session.is_completed ? 'bg-gray-50/50 border-gray-200 opacity-60' : 'bg-white border-gray-100 hover:border-[hsl(var(--accent))]/20'
              }`}
              data-testid={`admin-session-${session.id}`}
            >
              {/* Completed checkbox */}
              <button
                onClick={() => handleMarkCompleted(session)}
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                  session.is_completed ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 hover:border-[hsl(var(--accent))]'
                }`}
                title={session.is_completed ? 'Marcar como pendiente' : 'Marcar como realizada'}
                data-testid={`session-complete-${session.id}`}
              >
                {session.is_completed && <Check className="w-3 h-3" />}
              </button>

              <div className="flex-1 min-w-0">
                <span className={`font-medium text-sm ${session.is_completed ? 'text-gray-400 line-through' : 'text-[hsl(var(--text-primary))]'}`}>
                  {session.title}
                </span>
                <div className="flex items-center gap-3 text-xs text-[hsl(var(--text-tertiary))] mt-0.5">
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(session.session_date)}</span>
                  {session.start_time && (
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{session.start_time}{session.end_time ? ` - ${session.end_time}` : ''}</span>
                  )}
                  {session.meet_url && (
                    <a href={session.meet_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-500 hover:text-blue-600">
                      <ExternalLink className="w-3 h-3" />Meet
                    </a>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-1">
                <button onClick={() => startEdit(session)} className="p-1.5 text-gray-400 hover:text-[hsl(var(--accent))] rounded-md hover:bg-gray-50" title="Editar" data-testid={`edit-session-${session.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => handleDelete(session.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded-md hover:bg-red-50" title="Eliminar" data-testid={`delete-session-${session.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminClassCalendar;
